import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'

import {PullRequestErrorState} from '../components/PullRequestErrorState'

import {buildPullRequestsLayoutRoute} from './layout-route'
import {LayoutEntrypoint} from './Layout'
import {buildPullRequestsCommitsRoute} from './commits-route'
import {CommitsEntrypoint} from './Commits'
import {
  buildPullRequestsChangesRoute,
  buildPullRequestsChangesWithRangeRoute,
  buildPullRequestsFilesRoute,
  buildPullRequestsFilesWithRangeRoute,
} from './files-changed-routes'
import {ChangesEntrypoint, ChangesWithRangeEntrypoint, FilesEntrypoint, FilesWithRangeEntrypoint} from './FilesChanged'
import {buildPullRequestsConversationsRoute} from './conversations-route'
import {ConversationsEntrypoint} from './Conversations'
import {buildPullRequestsChecksRoute} from './checks-route'
import {ChecksEntrypoint} from './Checks'
import {buildPullRequestsOverviewRoute} from './overview-route'
import {OverviewEntrypoint} from './Overview'
import {buildPullRequestsFindingsRoute} from './findings-route'
import {FindingsEntrypoint} from './FindingsRoute'

interface CreatePullRequestsRoutesParams {
  isEnabled: (feature: string) => boolean | undefined
}

// Shared managed error boundary for every PR route. `toRoute` wraps this in a
// self-reporting boundary (route-level boundaries bypass React 19's onCaughtError,
// so failbot reporting must be restored), defaulting the failbot boundary name to
// each route's id and `critical` to true.
const errorBoundary = {fallback: <PullRequestErrorState />}

/**
 * Builds the Pull Requests route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `pull-requests`-bound singletons directly.
 *
 * `isEnabled` reads the app's server-provided feature state (embedded data). The
 * New Files Changed route set is included when `react_data_router_pull_request_files`
 * is enabled for the request; without it, the condensed commits layout is used.
 */
export function createPullRequestsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  {isEnabled}: CreatePullRequestsRoutesParams,
): RouteObject[] {
  const children: RouteObject[] = [
    buildPullRequestsConversationsRoute(appBuilder).toRoute({
      Component: ConversationsEntrypoint,
      errorBoundary,
    }),
    buildPullRequestsChecksRoute(appBuilder).toRoute({
      Component: ChecksEntrypoint,
      errorBoundary,
    }),
    // We unconditionally include the overview route but will manage access using the layout data's
    // feature state. This differs from new files changed because use of `add_client_feature_flag`
    // for complex / non-user FFs is deprecated.
    buildPullRequestsOverviewRoute(appBuilder).toRoute({
      Component: OverviewEntrypoint,
      errorBoundary,
    }),
    // The Rails controller enforces Findings access, while the layout feature state controls
    // whether navigation to this route is exposed in the UI.
    buildPullRequestsFindingsRoute(appBuilder).toRoute({
      Component: FindingsEntrypoint,
      errorBoundary,
    }),
  ]

  children.push(
    buildPullRequestsCommitsRoute(appBuilder).toRoute({
      Component: CommitsEntrypoint,
      errorBoundary,
    }),
  )

  // The `react_data_router_pull_request_files` feature flag is set in the controller when the
  // user is enrolled in the New Files Changed feature preview.
  if (isEnabled('react_data_router_pull_request_files')) {
    children.push(
      buildPullRequestsFilesRoute(appBuilder).toRoute({
        Component: FilesEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsFilesWithRangeRoute(appBuilder).toRoute({
        Component: FilesWithRangeEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsChangesRoute(appBuilder).toRoute({
        Component: ChangesEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsChangesWithRangeRoute(appBuilder).toRoute({
        Component: ChangesWithRangeEntrypoint,
        errorBoundary,
      }),
    )
  }

  return [
    buildPullRequestsLayoutRoute(appBuilder).toRoute({
      errorBoundary,
      Component: LayoutEntrypoint,
      children,
    }),
  ]
}
